import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import { Helmet } from "react-helmet-async";
import api from "@/lib/api";

const CATS = ["All", "Markets", "IPO", "Real Estate", "Commodities", "Crypto", "Economy"];

const REGIONS = [
  { key: "auto", label: "Auto (Geo)" },
  { key: "in", label: "🇮🇳 India" },
  { key: "us", label: "🇺🇸 US" },
  { key: "gb", label: "🇬🇧 UK" },
  { key: "fr", label: "🇫🇷 France" },
  { key: "de", label: "🇩🇪 Germany" },
  { key: "jp", label: "🇯🇵 Japan" },
  { key: "sg", label: "🇸🇬 SE Asia" },
  { key: "ae", label: "🇦🇪 Middle East" },
];

function timeAgo(iso) {
  const d = new Date(iso); const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}d`;
}

export default function News() {
  const [cat, setCat] = useState("All");
  // "auto" lets the backend geo-detect; manual choice persists in localStorage.
  const [region, setRegion] = useState(() => localStorage.getItem("ns_region") || "auto");
  useEffect(() => { localStorage.setItem("ns_region", region); }, [region]);

  const regionParam = region === "auto" ? "" : `&region=${region}`;
  const { data, isLoading } = useQuery({
    queryKey: ["news-full", cat, region],
    queryFn: async () => (await api.get(`/news?limit=40&category=${encodeURIComponent(cat)}${regionParam}`)).data,
    refetchInterval: 90_000,
    staleTime: 60_000,
  });
  const items = data?.data || [];
  const activeRegion = data?.region || "in";

  return (
    <div className="px-4 sm:px-6 lg:px-10 py-8 max-w-[1200px] mx-auto">
      <Helmet>
        <title>Market News — Real-time financial headlines · Nivesh Street</title>
        <meta name="description" content="Real-time financial market news curated for your country — India, US, UK, France, Germany, Japan and more." />
        <link rel="canonical" href="https://niveshstreet.in/news" />
      </Helmet>

      <div className="ns-eyebrow mb-2">Live Newsroom</div>
      <h1 className="font-heading font-extrabold text-4xl sm:text-5xl tracking-tight text-[#1A1A1A] mb-2">Market News</h1>
      <p className="text-[#6B6C6F] mb-6 max-w-2xl text-sm">
        Real-time financial headlines — curated for <span className="text-[#1A1A1A] font-semibold">{(REGIONS.find(r => r.key === activeRegion) || REGIONS[1]).label}</span>.
        Pick another market below.
      </p>

      {/* Region selector */}
      <div className="flex flex-wrap gap-2 mb-4" data-testid="news-region-switcher">
        {REGIONS.map(r => (
          <button
            key={r.key}
            onClick={() => setRegion(r.key)}
            className={`text-xs px-3 py-1.5 rounded-full font-semibold border transition-colors ${
              region === r.key
                ? "bg-[#1A1A1A] text-white border-[#1A1A1A]"
                : "bg-white text-[#3A3B3E] border-[#E2DDD3] hover:border-[#1A1A1A]/40"
            }`}
            data-testid={`region-pill-${r.key}`}
          >{r.label}</button>
        ))}
      </div>

      {/* Category filter */}
      <div className="flex flex-wrap gap-2 mb-6">
        {CATS.map(c => (
          <button
            key={c}
            onClick={() => setCat(c)}
            className={`text-xs px-3 py-1.5 rounded-full font-semibold border transition-colors ${
              cat === c
                ? "bg-[#D91E2A] text-white border-[#D91E2A]"
                : "bg-white text-[#3A3B3E] border-[#E2DDD3] hover:border-[#D91E2A]/40"
            }`}
            data-testid={`news-cat-${c.toLowerCase().replace(/\s+/g, "-")}`}
          >{c}</button>
        ))}
      </div>

      <div className="ns-card divide-y divide-[#E2DDD3]">
        {isLoading && <div className="p-6 text-sm text-[#8C8D90] font-mono-fin">Loading feed…</div>}
        {items.map(n => (
          <Link
            key={n.id || n.slug}
            to={`/news/${n.slug || n.id}`}
            className="flex gap-4 px-5 py-4 hover:bg-[#FAF8F3] transition-colors group"
            data-testid={`news-item-${n.id || n.slug}`}
          >
            {n.image ? (
              <img
                src={n.image}
                alt={n.title || ""}
                className="w-24 h-24 sm:w-28 sm:h-24 object-cover rounded flex-shrink-0 bg-[#F0ECE4]"
                loading="lazy"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
            ) : null}
            <div className="flex items-start justify-between gap-3 flex-1 min-w-0">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="ns-eyebrow">{n.category || "Markets"}</span>
                  <span className="text-[11px] text-[#8C8D90] font-mono-fin">· {n.source} · {timeAgo(n.published)} ago</span>
                </div>
                <h4 className="font-heading text-lg font-bold text-[#1A1A1A] group-hover:text-[#D91E2A] leading-snug">{n.title}</h4>
                {n.summary && (
                  <p className="text-sm text-[#6B6C6F] mt-1 line-clamp-2">{n.summary.replace(/<[^>]*>/g, "")}</p>
                )}
              </div>
              <ExternalLink className="w-3.5 h-3.5 text-[#8C8D90] group-hover:text-[#D91E2A] mt-1 flex-shrink-0" />
            </div>
          </Link>
        ))}
        {!isLoading && items.length === 0 && <div className="p-6 text-sm text-[#8C8D90] font-mono-fin">No news right now for this region.</div>}
      </div>
    </div>
  );
}
